import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Analytics } from "@/utils/analytics";
import { 
  Shield, 
  Laptop, 
  Clock, 
  TrendingDown, 
  CheckCircle, 
  ArrowRight,
  Calculator,
  Minus,
  Plus
} from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1632893037506-aac33bf18107?w=1200";
const SERVER_IMAGE = "https://images.unsplash.com/photo-1691435828932-911a7801adfb?w=800";

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    Analytics.pageView('landing');
  }, []);

  // Savings calculator state
  const [calcDeviceType, setCalcDeviceType] = useState("laptop");
  const [calcCount, setCalcCount] = useState(10);

  const devicePricing = {
    laptop: { label: "Laptops", market: 119900, oneIT: 106711, icon: "💻" },
    desktop: { label: "Desktops", market: 149900, oneIT: 134900, icon: "🖥️" },
    tablet: { label: "Tablets", market: 64900, oneIT: 59708, icon: "📱" },
    phone: { label: "Phones", market: 134900, oneIT: 126806, icon: "📲" }
  };

  const selected = devicePricing[calcDeviceType];
  const totalMarket = selected.market * calcCount;
  const totalOneIT = selected.oneIT * calcCount;
  const totalSavings = totalMarket - totalOneIT;
  const savingsPct = Math.round((totalSavings / totalMarket) * 100);

  const features = [
    {
      icon: Shield,
      title: "Verified Business Access",
      description: "Exclusive access for validated businesses. Sign in with your business email and trade license."
    },
    {
      icon: Laptop,
      title: "Curated Solutions",
      description: "Pre-configured bundles designed for your company size and industry needs."
    },
    {
      icon: Clock,
      title: "Clear Lead Times",
      description: "Know exactly when your hardware and solutions will be delivered and installed."
    },
    {
      icon: TrendingDown,
      title: "TCO Transparency",
      description: "View your Total Cost of Ownership and compare against market rates."
    }
  ];

  const stats = [
    { value: "₹75Cr+", label: "Saved by SMBs" },
    { value: "500+", label: "Businesses Served" },
    { value: "15%", label: "Avg. TCO Reduction" },
    { value: "PAN India", label: "Delivery Coverage" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/6nsrlqnk_Gemini_Generated_Image_wzvx61wzvx61wzvx.png" alt="OneIT" className="w-14 h-14 object-contain" />
            <span className="font-heading font-bold text-xl text-foreground">OneIT</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#savings" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Savings</a>
            <a href="#benefits" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Benefits</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn" className="text-foreground">
              Sign In
            </Button>
            <Button onClick={() => navigate("/login")} className="bg-[#007AFF] text-white hover:bg-[#0055CC]" data-testid="header-get-started-btn">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#007AFF]/5 border border-[#007AFF]/15">
                <span className="w-2 h-2 rounded-full bg-[#16a34a] animate-pulse" />
                <span className="text-sm text-muted-foreground">For Verified Businesses Only</span>
              </div>
              
              <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-tight text-foreground">
                IT buying,
                <br />
                <span className="text-gradient">without the headache.</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
                OEM-backed devices, software, and support for startups and SMBs — with one platform, one quote, and one accountable partner.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate("/login")}
                  className="bg-[#007AFF] text-white hover:bg-[#0055CC] btn-hover-scale"
                  data-testid="hero-get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-border hover:bg-secondary"
                  data-testid="hero-view-solutions-btn"
                  onClick={() => navigate("/login")}
                >
                  View Solutions
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-6 pt-4">
                {["Buy the right IT", "Transparent business pricing", "Expert guidance", "Full lifecycle support"].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-[#16a34a]" />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative animate-slide-up delay-200">
              <div className="relative rounded-2xl overflow-hidden border border-border shadow-lg">
                <img src={HERO_IMAGE} alt="Modern workspace" className="w-full h-[400px] object-cover" />
              </div>
              
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-xl p-4 shadow-xl animate-slide-in-left delay-400">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#16a34a]/10 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-[#16a34a]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-heading text-foreground">25%</p>
                    <p className="text-xs text-muted-foreground">Avg. Savings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 px-6 border-b border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground mb-8">Trusted by growing businesses across India. Apple Authorized Reseller. 15+ years enterprise experience.</p>
          <div className="flex flex-wrap items-center justify-center gap-10">
            <img src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/2ibqh5l0_apple-logo-wallpaper-dark-background-apple-ios-macos-logo-symbol-d-black-background-shiny-glass-material-ideal-143126220.webp" alt="Apple" className="h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/pp80k8bk_b1b9b44aa929f2f1734e1d4e4f17dad5.jpg" alt="Samsung" className="h-10 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/xqn1snak_HP%20logo.png" alt="HP" className="h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/5zqmz668_scalefusion_logo.jpeg" alt="Scalefusion" className="h-10 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl md:text-4xl font-bold font-heading text-[#007AFF]">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-[#007AFF] mb-4">Features</p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
              Everything You Need
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Streamlined IT procurement designed specifically for growing businesses
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-6 card-glow category-card shadow-sm">
                <div className="w-12 h-12 rounded-lg bg-[#007AFF]/8 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2 text-foreground">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="benefits" className="py-24 px-6 bg-secondary/40">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-[#007AFF] mb-4">How It Works</p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-8 text-foreground">
                Simple. Fast. Transparent.
              </h2>
              
              <div className="space-y-6">
                {[
                  { step: "01", title: "Verify Your Business", desc: "Sign in with your business email and upload your trade license" },
                  { step: "02", title: "Browse Solutions", desc: "Explore curated bundles based on your company size" },
                  { step: "03", title: "Compare & Configure", desc: "View pricing, TCO, and customize your selection" },
                  { step: "04", title: "Procure with Confidence", desc: "Place your order with clear lead times and support" }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-card border border-border flex items-center justify-center shrink-0 shadow-sm">
                      <span className="font-mono text-sm text-[#007AFF]">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold mb-1 text-foreground">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-border shadow-lg">
                <img src={SERVER_IMAGE} alt="Server room" className="w-full h-[500px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Savings Calculator */}
      <section id="savings" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#16a34a]/8 border border-[#16a34a]/15 mb-6">
              <Calculator className="w-4 h-4 text-[#16a34a]" />
              <span className="text-sm text-[#16a34a] font-medium">Savings Calculator</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4 text-foreground">
              See How Much You Could Save
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Estimate your savings on IT procurement with OneIT's business pricing vs typical market rates.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden" data-testid="savings-calculator">
            {/* Calculator controls */}
            <div className="p-6 md:p-8 space-y-6">
              {/* Device type selector */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">Device Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(devicePricing).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => setCalcDeviceType(key)}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
                        calcDeviceType === key
                          ? "bg-[#007AFF] text-white border-[#007AFF] shadow-md"
                          : "bg-secondary border-border text-foreground hover:border-[#007AFF]/30"
                      }`}
                      data-testid={`calc-device-${key}`}
                    >
                      <span className="block text-lg mb-1">{val.icon}</span>
                      {val.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity slider */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">Number of Devices</label>
                  <span className="text-sm font-bold text-[#007AFF]">{calcCount} units</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCalcCount(Math.max(1, calcCount - 1))}
                    className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                    data-testid="calc-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={calcCount}
                    onChange={(e) => setCalcCount(parseInt(e.target.value))}
                    className="flex-1 accent-[#007AFF]"
                    data-testid="calc-slider"
                  />
                  <button
                    onClick={() => setCalcCount(Math.min(50, calcCount + 1))}
                    className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                    data-testid="calc-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-[#007AFF]/[0.03] border-t border-border p-6 md:p-8">
              <div className="grid grid-cols-3 gap-4 md:gap-8">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Market Price</p>
                  <p className="text-lg md:text-xl font-bold text-foreground/60 line-through">
                    ₹{totalMarket.toLocaleString()}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">OneIT Price</p>
                  <p className="text-lg md:text-xl font-bold text-[#007AFF]">
                    ₹{totalOneIT.toLocaleString()}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">You Save</p>
                  <p className="text-lg md:text-xl font-bold text-[#16a34a]">
                    ₹{totalSavings.toLocaleString()}
                  </p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[#16a34a]/10 text-[#16a34a] text-xs font-semibold">
                    {savingsPct}% off
                  </span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-[#007AFF] to-[#0055CC] px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="font-heading font-semibold text-white">Want exact pricing for your business?</p>
                <p className="text-sm text-white/70">Sign in with your business email to unlock full pricing details.</p>
              </div>
              <Button 
                onClick={() => navigate("/login")}
                className="bg-white text-[#007AFF] hover:bg-white/90 whitespace-nowrap font-semibold"
                data-testid="savings-cta-btn"
              >
                View Full Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-secondary/40">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground">
            Ready to Transform Your IT Procurement?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join hundreds of SMBs who've reduced their TCO and streamlined their IT buying process.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate("/login")}
            className="bg-[#007AFF] text-white hover:bg-[#0055CC] btn-hover-scale"
            data-testid="cta-get-started-btn"
          >
            Get Started Now
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/6nsrlqnk_Gemini_Generated_Image_wzvx61wzvx61wzvx.png" alt="OneIT" className="w-14 h-14 object-contain" />
              <span className="font-heading font-bold text-foreground">OneIT</span>
            </div>
            <p className="text-sm text-muted-foreground">
              &copy; 2025 OneIT. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
